import React, { useMemo } from "react";
import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Typography,
    type SelectChangeEvent,
} from "@mui/material";
import { FirstPage, LastPage, KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";

type PageIndicator = number | "ellipsis";

interface CollectionPaginationProps {
    count: number;
    page: number;
    rowsPerPage: number;
    rowsPerPageOptions?: number[];
    onPageChange: (newPage: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    itemLabel?: string;
}

const defaultRowsPerPageOptions = [10, 25, 50];

const buildPageIndicators = (currentPage: number, totalPages: number): PageIndicator[] => {
    if (totalPages <= 6) {
        return Array.from({ length: totalPages }, (_, index) => index);
    }

    const candidatePages = new Set<number>([0, totalPages - 1, currentPage, currentPage - 1, currentPage + 1]);
    candidatePages.add(currentPage - 2);
    candidatePages.add(currentPage + 2);

    const sortedPages = Array.from(candidatePages)
        .filter((value) => value >= 0 && value < totalPages)
        .sort((a, b) => a - b);

    const indicators: PageIndicator[] = [];
    sortedPages.forEach((value, index) => {
        if (index > 0 && value - sortedPages[index - 1] > 1) {
            indicators.push("ellipsis");
        }
        indicators.push(value);
    });

    return indicators;
};

const CollectionPagination: React.FC<CollectionPaginationProps> = ({
    count,
    page,
    rowsPerPage,
    rowsPerPageOptions,
    onPageChange,
    onRowsPerPageChange,
    itemLabel = "items",
}) => {
    const totalPages = Math.max(1, Math.ceil(count / rowsPerPage));
    const currentPage = Math.min(page, totalPages - 1);
    const from = count === 0 ? 0 : currentPage * rowsPerPage + 1;
    const to = count === 0 ? 0 : Math.min(count, (currentPage + 1) * rowsPerPage);

    const pageIndicators = useMemo(
        () => buildPageIndicators(currentPage, totalPages),
        [currentPage, totalPages]
    );

    const rowsOptions = useMemo(() => {
        const options = rowsPerPageOptions?.length ? rowsPerPageOptions : defaultRowsPerPageOptions;
        return Array.from(new Set([...options, rowsPerPage])).sort((a, b) => a - b);
    }, [rowsPerPageOptions, rowsPerPage]);

    const handleRowsPerPageChange = (event: SelectChangeEvent<string | number>) => {
        const value = Number(event.target.value);
        if (!Number.isNaN(value)) {
            onRowsPerPageChange(value);
        }
    };

    const goToPage = (newPage: number) => {
        if (newPage >= 0 && newPage < totalPages && newPage !== currentPage) {
            onPageChange(newPage);
        }
    };

    const buttonSx = {
        border: "1px solid #BBDCFB",
        borderRadius: "6px",
        minWidth: 32,
        height: 32,
        color: "#7498A6",
        fontSize: "0.85rem",
        "&:hover": {
            borderColor: "#7498A6",
        },
    } as const;

    const iconButtonSx = {
        ...buttonSx,
        padding: 0,
        "&.Mui-disabled": {
            color: "#BBDCFB",
            borderColor: "#D9E8F4",
        },
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
            >
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ color: "#7498A6" }}
                >
                    <Typography variant="body2" sx={{ color: "#7498A6" }}>
                        Rows per page
                    </Typography>
                    <Select
                        size="small"
                        value={String(rowsPerPage)}
                        onChange={handleRowsPerPageChange}
                        sx={{
                            minWidth: 72,
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#BBDCFB",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#7498A6",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#7498A6",
                            },
                        }}
                    >
                        {rowsOptions.map((option) => (
                            <MenuItem key={option} value={String(option)}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{
                        color: "#7498A6",
                        flexGrow: 1,
                        justifyContent: { xs: "flex-start", md: "center" },
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={() => goToPage(0)}
                        disabled={currentPage === 0}
                        sx={iconButtonSx}
                    >
                        <FirstPage fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 0}
                        sx={iconButtonSx}
                    >
                        <KeyboardArrowLeft fontSize="small" />
                    </IconButton>
                    {pageIndicators.map((indicator, index) =>
                        indicator === "ellipsis" ? (
                            <Typography key={`ellipsis-${index}`} variant="body2" color="#7498A6" sx={{ px: 0.5 }}>
                                …
                            </Typography>
                        ) : (
                            <Button
                                key={indicator}
                                size="small"
                                onClick={() => goToPage(indicator)}
                                sx={{
                                    ...buttonSx,
                                    borderColor: indicator === currentPage ? "#F28D7A" : "#BBDCFB",
                                    backgroundColor: indicator === currentPage ? "#F28D7A" : "transparent",
                                    color: indicator === currentPage ? "#fff" : "#7498A6",
                                }}
                            >
                                {indicator + 1}
                            </Button>
                        )
                    )}
                    <IconButton
                        size="small"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage >= totalPages - 1}
                        sx={iconButtonSx}
                    >
                        <KeyboardArrowRight fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => goToPage(totalPages - 1)}
                        disabled={currentPage >= totalPages - 1}
                        sx={iconButtonSx}
                    >
                        <LastPage fontSize="small" />
                    </IconButton>
                </Stack>
                <Typography
                    variant="body2"
                    sx={{
                        color: "#7498A6",
                        whiteSpace: "nowrap",
                        textAlign: { xs: "left", md: "right" },
                        minWidth: "fit-content",
                    }}
                >
                    {`Showing ${from}-${to} of ${count} ${itemLabel}`}
                </Typography>
            </Stack>
        </Box>
    );
};

export default CollectionPagination;
